import {
	FontAwesome,
	MaterialCommunityIcons,
	MaterialIcons
} from "@expo/vector-icons";
import { useNavigation, useRoute } from "@react-navigation/core";
import * as Clipboard from "expo-clipboard";
import React, { memo, useEffect, useRef } from "react";
import {
	Image,
	Keyboard,
	KeyboardAvoidingView,
	Text,
	TouchableOpacity,
	View
} from "react-native";
import { Input } from "react-native-elements";
import { Pressable, ScrollView } from "react-native-gesture-handler";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { getYoutubeMeta } from "react-native-youtube-iframe";
import { useDispatch, useSelector } from "react-redux";
import {
	CLEAR_READY_TO_COPY_URL,
	CLEAR_TEMPLATE,
	CLEAR_TEMPLATE_PHOTO_DATA,
	CLEAR_TEMPLATE_VALIDATE,
	SET_DISPLAY_PREVIEW,
	SET_KEYBOARD_OPEN,
	SET_SHOULD_PROVIDE_CATEGORY,
	SET_TEMPLATE_CATEGORY_NAME_EN,
	SET_TEMPLATE_CATEGORY_NAME_FR,
	SET_TEMPLATE_DESCRIPTION_EN,
	SET_TEMPLATE_DESCRIPTION_FR,
	SET_TEMPLATE_ENGLISH,
	SET_TEMPLATE_NAME,
	SET_TEMPLATE_PHOTO_DATA,
	SET_TEMPLATE_POST_DESCRIPTION_EN,
	SET_TEMPLATE_POST_DESCRIPTION_FR,
	SET_TEMPLATE_THUMBNAIL_URL,
	SET_TEMPLATE_TITLE_EN,
	SET_TEMPLATE_TITLE_FR,
	SET_TEMPLATE_VIDEO_ID,
	SET_TEMPLATE_URL,
	VALIDATE_TEMPLATE_INPUTS,
	SET_READY_TO_COPY_URL
} from "../actions/types";
import { getImageUri } from "../assets/Images";

import { fetchSaveTemplate } from "../actions/template";
import LiveEvent from "../components/LiveEvent";
import { GROUP_ID_TEMPLATE } from "../config/constants";
import { t } from "../services/i18n";
import commonStyles, { COLOR2, isiOS } from "../styles/commonStyles";
import { pickImage } from "../utils/PickImage";
import { pickDocument } from "../utils/PickDocument";
import MediaModal from "../components/MediaModal";

const CreateTemplate = () => {
	const dispatch = useDispatch();
	const route = useRoute();
	const navigation = useNavigation();

	const nameInputRef = useRef();
	const descriptionInputRef = useRef();
	const categoryInputRef = useRef();
	const insets = useSafeAreaInsets();
	const mediaModal = useRef();

	const {
		isFetching,
		name,
		files,
		photoUri,
		title,
		description,
		postDescription,
		descriptionErrorMsg,
		url,
		urlErrorMsg,
		lang,
		clearTemplateValidate,
		isValid,
		isEnglish,
		readyToCopyUrl,
		youtubeVideoId,
		saveTemplateResult,
		category,
		displayPreview,
		nameErrorMsg,
		categoryErrorMsg
	} = useSelector(state => state.templateReducer);

	const shouldProvideCategory = !Boolean(route.params?.categoryId);

	useEffect(() => {
		dispatch({ type: SET_DISPLAY_PREVIEW, value: false });
		dispatch({
			type: SET_SHOULD_PROVIDE_CATEGORY,
			value: shouldProvideCategory
		});

		const keyboardWillShowSub = Keyboard.addListener(
			isiOS ? "keyboardWillShow" : "keyboardDidShow",
			() => {
				dispatch({ type: SET_DISPLAY_PREVIEW, value: false });
				dispatch({ type: SET_KEYBOARD_OPEN, value: true });
			}
		);

		const keyboardWillHideSub = Keyboard.addListener(
			isiOS ? "keyboardWillHide" : "keyboardDidHide",
			() => {
				dispatch({ type: SET_DISPLAY_PREVIEW, value: true });
				dispatch({ type: SET_KEYBOARD_OPEN, value: false });
			}
		);

		return () => {
			keyboardWillShowSub.remove();
			keyboardWillHideSub.remove();
		};
	}, []);

	useEffect(() => {
		if (clearTemplateValidate) {
			// Validate inputs
			dispatch({
				type: VALIDATE_TEMPLATE_INPUTS
			});
		}
	}, [clearTemplateValidate]);

	useEffect(() => {
		if (descriptionErrorMsg) {
			descriptionInputRef.current?.shake();
			descriptionInputRef.current?.focus();
		}
	}, [descriptionErrorMsg]);

	useEffect(() => {
		if (categoryErrorMsg) {
			categoryInputRef.current?.shake();
			categoryInputRef.current?.focus();
		}
	}, [categoryErrorMsg]);

	useEffect(() => {
		if (nameErrorMsg) {
			nameInputRef.current?.shake();
			nameInputRef.current?.focus();
		}
	}, [nameErrorMsg]);

	useEffect(() => {
		if (displayPreview) {
			Keyboard.dismiss();
		}
	}, [displayPreview]);

	useEffect(() => {
		if (isValid) {
			dispatch(
				fetchSaveTemplate({
					files,
					categoryId: route.params?.categoryId,
					name,
					title,
					description,
					postDescription,
					photoUri,
					youtubeVideoId,
					type: "Template",
					subtype: "Post",
					lang,
					category,
					url
				})
			);
		}
	}, [isValid]);

	useEffect(() => {
		if (saveTemplateResult) {
			dispatch({ type: CLEAR_TEMPLATE });
			// `popTo` (not `navigate`) is required to actually pop the screens
			// above TabScreen — see SelectTemplateDate.js for the rationale.
			navigation.popTo("TabScreen", {
				screen: "LiveTab",
				params: {
					screen: "LiveScreen",
					params: { communityUri: route.params?.communityUri }
				}
			});
		}
	}, [saveTemplateResult]);

	const {
		bgColor1,
		bgWhite,
		red,
		row,
		flex1,
		alignItemsCenter,
		justifyContentSpaceBetween,
		mt10,
		font,
		fontBold,
		shadowGrey,
		ml10,
		fs14,
		fs16,
		gap10,
		mb10,
		pv20
	} = commonStyles;

	const _pickImage = async () => {
		const image = await pickImage(dispatch);

		if (image) {
			dispatch({
				type: SET_TEMPLATE_PHOTO_DATA,
				...image
			});
		}
	};

	const _pickDocument = async () => {
		const document = await pickDocument(dispatch);

		if (document) {
			dispatch({
				type: SET_TEMPLATE_PHOTO_DATA,
				...document
			});
		}
	};

	const renderStickyButtons = () => {
		return (
			<View
				style={[
					row,
					{
						position: "absolute",
						bottom: 0,
						left: 0,
						right: 0,
						borderTopWidth: 1,
						borderTopColor: "#e0e0e0"
					}
				]}
			>
				<TouchableOpacity
					underlayColor="transparent"
					activeOpacity={0.8}
					onPress={() => {
						dispatch({ type: CLEAR_TEMPLATE });
						navigation.popTo("TabScreen", {
							screen: "LiveTab",
							params: {
								screen: "LiveScreen",
								params: { communityUri: route.params?.communityUri }
							}
						});
					}}
					style={[
						{
							height: 60 + insets.bottom,
							flex: 1,
							justifyContent: "center",
							alignItems: "center",
							backgroundColor: "white"
						}
					]}
				>
					<Text style={[fs16, fontBold, red]}>{t("button:cancel")}</Text>
				</TouchableOpacity>
				<TouchableOpacity
					underlayColor="transparent"
					activeOpacity={0.8}
					onPress={() =>
						dispatch({
							type: CLEAR_TEMPLATE_VALIDATE
						})
					}
					disabled={isFetching}
					style={[
						{
							height: 60 + insets.bottom,
							flex: 1,
							justifyContent: "center",
							alignItems: "center",
							backgroundColor: COLOR2,
							borderLeftWidth: 1,
							borderLeftColor: "#e0e0e0"
						}
					]}
				>
					<Text style={[fontBold, fs16, { color: "white" }]}>
						{t("button:add")}
					</Text>
				</TouchableOpacity>
			</View>
		);
	};

	const onFocus = async () => {
		if (readyToCopyUrl) {
			try {
				const text = `${await Clipboard.getStringAsync()} `;
				let match;
				const re =
					/^((http:\/\/|https:\/\/|www.)\S+?\.(?:jpe?g|gif|png|tiff|svg))(\&[a-zA-Z0-9=]+)?[.! ]$/;
				if (re.test(text)) {
					dispatch({
						type: SET_TEMPLATE_PHOTO_DATA,
						value: text.trim()
					});
					if (text.indexOf("giphy") !== -1 && !postDescription) {
						dispatch({
							type: SET_TEMPLATE_POST_DESCRIPTION_FR,
							value: `Image provenant de Giphy`
						});
					}
				} else if (
					(match = text.match(
						/^.*(https:\/\/www.|https:\/\/)youtube.com\/watch\?v=([a-zA-Z0-9-]+)(\&[a-zA-Z0-9=_]+)?[\n.!, ]$/
					)) ||
					(match = text.match(
						/^.*(https:\/\/www.|https:\/\/)youtu.be\/([-_a-zA-Z0-9]+)(\?\S+=\S+)?[\n.!, ]$/
					)) ||
					(match = text.match(
						/^.*(https:\/\/www.|https:\/\/)www.youtube.com\/live\/([-_a-zA-Z0-9]+)(\?\S+=\S+)?[\n.!, ]$/
					))
				) {
					dispatch({
						type: SET_TEMPLATE_VIDEO_ID,
						value: match[2]
					});
					const videoMeta = await getYoutubeMeta(match[2]);
					if (!title) {
						dispatch({
							type: SET_TEMPLATE_DESCRIPTION_FR,
							value: videoMeta.title
						});
					}
					if (!postDescription) {
						dispatch({
							type: SET_TEMPLATE_POST_DESCRIPTION_FR,
							value: `Vidéo Youtube de ${videoMeta.author_name}`
						});
					}
					dispatch({
						type: SET_TEMPLATE_THUMBNAIL_URL,
						value: videoMeta.thumbnail_url
					});
				}
			} catch (error) {
				console.error(error);
			} finally {
				dispatch({ type: CLEAR_READY_TO_COPY_URL });
			}
		}
	};

	const handleOnChangeText = async content => {
		const regEnd = /^[-_a-zA-Z0-9][\n.!, ]$/;
		let oldContent = "";
		if (isEnglish) {
			if (lang?.en?.description) {
				oldContent = lang.en.description;
			}
		} else if (description) {
			oldContent = description;
		}
		if (
			content === "" ||
			photoUri ||
			youtubeVideoId ||
			oldContent.length > content.length ||
			content.length < 8 ||
			!regEnd.test(content.substring(content.length - 2))
		) {
			return;
		}
		let match;
		if (
			(match = content.match(
				/^.*((http:\/\/|https:\/\/|www.)\S+?\.(?:jpe?g|jpg|gif|png|tiff|svg))[\n.!, ]$/
			))
		) {
			dispatch({
				type: SET_TEMPLATE_PHOTO_DATA,
				value: match[1]
			});
		}
		if (
			(match = content.match(
				/^.*(https:\/\/www.|https:\/\/)youtube.com\/watch\?v=([a-zA-Z0-9-_]+)(\&[a-zA-Z0-9=-_]+)?[\n.!, ]$/
			)) ||
			(match = content.match(
				/^.*(https:\/\/www.|https:\/\/)youtu.be\/([-_a-zA-Z0-9]+)(\?\S+=\S+)?[\n.!, ]$/
			))
		) {
			dispatch({
				type: SET_TEMPLATE_VIDEO_ID,
				value: match[2]
			});
			const videoMeta = await getYoutubeMeta(match[2]);
			if (!description) {
				dispatch({ type: SET_TEMPLATE_DESCRIPTION_FR, value: videoMeta.title });
			}
			if (!postDescription) {
				dispatch({
					type: SET_TEMPLATE_POST_DESCRIPTION_FR,
					value: `Vidéo Youtube de ${videoMeta.author_name}`
				});
			}
			dispatch({
				type: SET_TEMPLATE_THUMBNAIL_URL,
				value: videoMeta.thumbnail_url
			});
		}
	};

	return (
		<KeyboardAvoidingView
			{...(isiOS && { behavior: "padding" })}
			style={[flex1, bgColor1]}
		>
			<MediaModal ref={mediaModal} />
			{displayPreview && (
				<LiveEvent
					key={`preview-${photoUri}`}
					item={{
						groupId: GROUP_ID_TEMPLATE,
						eventId: {
							title,
							description,
							postDescription,
							photoUri,
							youtubeVideoId,
							url
						}
					}}
				/>
			)}
			<ScrollView
				contentContainerStyle={[mt10, { paddingBottom: 80 + insets.bottom }]}
			>
				<View style={[bgWhite, shadowGrey, alignItemsCenter, pv20]}>
					<Pressable
						onPress={() =>
							dispatch({
								type: SET_TEMPLATE_ENGLISH,
								payload: isEnglish ? false : true
							})
						}
						style={[row, alignItemsCenter, mb10]}
					>
						<Image
							style={{ width: 24, height: 24 }}
							source={getImageUri(isEnglish ? "GB" : "FR")}
						/>
						<Text style={[ml10, font, fs14]}>
							{t(isEnglish ? "template:english" : "template:french")}
						</Text>
					</Pressable>
					<Input
						ref={nameInputRef}
						value={name || ""}
						onChangeText={value => dispatch({ type: SET_TEMPLATE_NAME, value })}
						onFocus={async () => {
							await onFocus();
						}}
						placeholder={t("template:name")}
						errorMessage={nameErrorMsg}
					/>
					{shouldProvideCategory &&
						(isEnglish ? (
							<Input
								value={category?.lang?.en?.name || ""}
								onChangeText={value =>
									dispatch({ type: SET_TEMPLATE_CATEGORY_NAME_EN, value })
								}
								placeholder={t("template:category")}
							/>
						) : (
							<Input
								ref={categoryInputRef}
								errorMessage={categoryErrorMsg}
								value={category?.name || ""}
								onChangeText={value =>
									dispatch({ type: SET_TEMPLATE_CATEGORY_NAME_FR, value })
								}
								placeholder={t("template:category")}
							/>
						))}
					{isEnglish ? (
						<Input
							value={lang?.en?.title ?? ""}
							onChangeText={value =>
								dispatch({ type: SET_TEMPLATE_TITLE_EN, value })
							}
							onFocus={async () => {
								await onFocus();
							}}
							placeholder={t("template:title")}
						/>
					) : (
						<Input
							value={title || ""}
							onChangeText={value =>
								dispatch({ type: SET_TEMPLATE_TITLE_FR, value })
							}
							onFocus={async () => {
								await onFocus();
							}}
							placeholder={t("template:title")}
						/>
					)}
					{isEnglish ? (
						<Input
							multiline
							value={lang?.en?.description ?? ""}
							onChangeText={value => {
								handleOnChangeText(value);
								dispatch({ type: SET_TEMPLATE_DESCRIPTION_EN, value });
							}}
							onFocus={async () => {
								await onFocus();
							}}
							placeholder={t("template:descriptionoptional")}
						/>
					) : (
						<Input
							ref={descriptionInputRef}
							multiline
							value={description || ""}
							onChangeText={value => {
								handleOnChangeText(value);
								dispatch({ type: SET_TEMPLATE_DESCRIPTION_FR, value });
							}}
							onFocus={async () => {
								await onFocus();
							}}
							errorMessage={descriptionErrorMsg}
							placeholder={
								photoUri
									? t("template:descriptionoptional")
									: t("template:description")
							}
						/>
					)}

					{photoUri ? (
						<View
							style={[row, alignItemsCenter, gap10, { marginVertical: 10 }]}
						>
							{files.some(f => f.base64?.startsWith("data:application/pdf")) ? (
								<View style={[row, alignItemsCenter, gap10]}>
									<MaterialCommunityIcons
										name="file-pdf-box"
										size={40}
										color={COLOR2}
									/>
									<Text style={[font, fs14]}>Document PDF</Text>
								</View>
							) : null}
							<TouchableOpacity
								style={[
									{
										width: 60,
										height: 60,
										backgroundColor: "red",
										borderRadius: 30,
										justifyContent: "center",
										alignItems: "center"
									}
								]}
								onPress={() => dispatch({ type: CLEAR_TEMPLATE_PHOTO_DATA })}
							>
								<MaterialCommunityIcons
									name={"delete-forever"}
									size={34}
									color="white"
								/>
							</TouchableOpacity>
						</View>
					) : (
						<View
							style={[
								row,
								justifyContentSpaceBetween,
								alignItemsCenter,
								gap10,
								{ paddingHorizontal: 20, marginVertical: 10 }
							]}
						>
							<TouchableOpacity
								style={[
									{
										width: 60,
										height: 60,
										backgroundColor: COLOR2,
										borderRadius: 30,
										justifyContent: "center",
										alignItems: "center",
										marginBottom: 8
									}
								]}
								onPress={() => {
									Keyboard.dismiss();
									navigation.navigate("CameraScreen", {
										from: "template"
									});
								}}
							>
								<MaterialIcons name="camera-alt" size={28} color="white" />
							</TouchableOpacity>
							<TouchableOpacity
								style={[
									{
										width: 60,
										height: 60,
										backgroundColor: COLOR2,
										borderRadius: 30,
										justifyContent: "center",
										alignItems: "center",
										marginBottom: 8
									}
								]}
								onPress={_pickImage}
							>
								<FontAwesome name="image" size={24} color="white" />
							</TouchableOpacity>
							<TouchableOpacity
								style={[
									{
										width: 60,
										height: 60,
										backgroundColor: COLOR2,
										borderRadius: 30,
										justifyContent: "center",
										alignItems: "center",
										marginBottom: 8
									}
								]}
								onPress={_pickDocument}
							>
								<MaterialCommunityIcons
									name="file-pdf-box"
									size={28}
									color="white"
								/>
							</TouchableOpacity>
							<TouchableOpacity
								style={[
									{
										width: 60,
										height: 60,
										backgroundColor: COLOR2,
										borderRadius: 30,
										justifyContent: "center",
										alignItems: "center",
										marginBottom: 8
									}
								]}
								onPress={() => {
									dispatch({ type: SET_READY_TO_COPY_URL });
									mediaModal.current?.open();
								}}
							>
								<FontAwesome name="link" size={24} color="white" />
							</TouchableOpacity>
						</View>
					)}
					{isEnglish ? (
						<Input
							multiline
							value={lang?.en?.postDescription ?? ""}
							onChangeText={value =>
								dispatch({ type: SET_TEMPLATE_POST_DESCRIPTION_EN, value })
							}
							onFocus={async () => {
								await onFocus();
							}}
							placeholder={t("template:postdescription")}
						/>
					) : (
						<Input
							multiline
							value={postDescription || ""}
							onChangeText={value =>
								dispatch({ type: SET_TEMPLATE_POST_DESCRIPTION_FR, value })
							}
							onFocus={async () => {
								await onFocus();
							}}
							placeholder={t("template:postdescription")}
						/>
					)}
					<Input
						value={url}
						keyboardType="url"
						inputMode="url"
						onChangeText={value => dispatch({ type: SET_TEMPLATE_URL, value })}
						onFocus={async () => {
							await onFocus();
						}}
						errorMessage={urlErrorMsg}
						placeholder={t("template:url")}
						autoCapitalize="none"
						autoCorrect={false}
						spellCheck={false}
						autoComplete="off"
					/>
				</View>
			</ScrollView>
			{renderStickyButtons()}
		</KeyboardAvoidingView>
	);
};

const MemoizedCreateTemplate = memo(CreateTemplate);
export default MemoizedCreateTemplate;
